// ConfigurationManagement/src/utils/ctx.js
const PLATFORM_HOSTS = [
  `development.${process.env.REACT_APP_HOST_DOMAIN}`,
  `staging.${process.env.REACT_APP_HOST_DOMAIN}`,
  `${process.env.REACT_APP_HOST_DOMAIN}`,
];

export function resolveEnv() {
  const meta = document.querySelector('meta[name="app-env"]')?.content;
  if (meta) return meta;
  const host = window.location.hostname;
  if (host.endsWith(`.development.${process.env.REACT_APP_API_SUFFIX}`))
    return "development";
  if (host.endsWith(`.staging.${process.env.REACT_APP_API_SUFFIX}`))
    return "staging";
  if (host.endsWith(`.${process.env.REACT_APP_API_SUFFIX}`))
    return "production";
  return "production";
}

/**
 * Hostname labels that are never a station (RCS-58).
 *
 * This list is deliberately a SECOND copy of knowledge that also lives in
 * api/src/modules/onboarding/lib/tenantIds.js as RESERVED_TENANT_IDS, and the
 * duplication is forced rather than lazy: api/Dockerfile.ec2 does `COPY api/ .`,
 * so the deployed API image contains no packages/ directory and cannot import
 * from here. If you add a name to one, add it to the other.
 *
 * It holds only what can appear as the FIRST LABEL of a host this code runs on.
 * The server list is longer because it also guards names nobody should be able
 * to register (smtp, billing, invite), which cannot reach this function.
 */
export const NON_STATION_HOST_LABELS = new Set([
  "default",
  "www",
  "admin",
  "api",
  "app",
  "embed",
  "static",
  "assets",
  "cdn",
  "test",
  "dev",
  "development",
  "development2",
  "staging",
  "prod",
  "production",
  "localhost",
  // Ours: the platform's own apex hosts, never a station.
  "radiocustoms",
  "creatorrabbit",
  "broadcastplug",
]);

const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/**
 * Which station a hostname belongs to, or "default" when it is not a station's
 * host at all.
 *
 * WHY THIS EXISTS. The old rule was "the first label IS the station, unless it
 * is www". That invents stations: on `localhost` it asked the API for a station
 * called "localhost"; on the dev2 apex it asked for "development2". Neither
 * exists, and both only worked because the config route quietly served the
 * `default` station's document for any unknown id -- which meant a genuine typo
 * or a stale embed silently played another broadcaster instead of failing.
 *
 * So this now names the hosts that are not stations, and answers "default" for
 * them. `default` is a real configured row, so those callers still resolve
 * through the ordinary path, and the backend is free to treat every OTHER
 * unknown id as the error it is.
 *
 * Pure and exported for its own sake: the rule is the whole ticket, and it is
 * worth testing without a browser.
 */
export function tenantFromHostname(hostname) {
  const host = String(hostname || "").trim().toLowerCase();
  if (!host) return "default";

  // Bare hosts (`localhost`, a container name, a Docker alias) have no station
  // label to read. An address is not a station either.
  if (!host.includes(".")) return "default";
  if (IPV4.test(host)) return "default";
  if (host.startsWith("[") || host.includes(":")) return "default"; // IPv6 literal

  const [label] = host.split(".");
  if (!label) return "default";
  if (NON_STATION_HOST_LABELS.has(label)) return "default";

  return label;
}

export function resolveTenant() {
  // An explicit answer always wins. The meta tag is written by the server that
  // knows which station it is serving, and ?tenant= is how a popout player
  // carries its station across a window. Neither is second-guessed here: if one
  // of them names a station that does not exist, the backend now says so
  // instead of quietly substituting another (RCS-58).
  const meta = document.querySelector('meta[name="app-tenant"]')?.content;
  if (meta) return meta;

  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get("tenant");
  if (tenantParam) return tenantParam;

  return tenantFromHostname(window.location.hostname);
}

/**
 * Whether this looks like a host of ours rather than a customer's own domain.
 *
 * Deliberately left on the old loose rule, and deliberately NOT reused to
 * decide which station we are on -- that is tenantFromHostname(), which is
 * stricter. The two questions differ: the dev2 apex is a host of ours and is
 * not a station. This one is read in a single console.log in each player's
 * bootstrapConfig and nothing branches on it, so tightening it would change a
 * log line and nothing else (RCS-58).
 */
export function isOnOurPlatform() {
  const [subdomain] = window.location.hostname.split(".");
  return subdomain && subdomain !== "www";
}